import React, {useEffect, useState} from 'react';
import {useSearchParams, Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {CheckCircle, Loader2} from 'lucide-react';
import {getCheckoutStatus} from '../utils/api';
import {Button} from '../components/ui/button';

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading');
    const [paymentDetails, setPaymentDetails] = useState(null);
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            return;
        }

        const pollStatus = async (attempts = 0) => {
            const maxAttempts = 5;
            if (attempts >= maxAttempts) {
                setStatus('timeout');
                return;
            }

            try {
                const data = await getCheckoutStatus(sessionId);
                setPaymentDetails(data);

                if (data.payment_status === 'paid') {
                    setStatus('success');
                } else if (data.status === 'expired') {
                    setStatus('expired');
                } else {
                    setTimeout(() => pollStatus(attempts + 1), 2000);
                }
            } catch (error) {
                console.error('Error checking payment status:', error);
                setStatus('error');
            }
        };

        pollStatus();
    }, [sessionId]);

    return (
        <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="payment-success-page">
            <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-2xl mx-auto">
                    {status === 'loading' && (
                        <motion.div className="glass rounded-3xl p-12 text-center" initial={{opacity: 0, scale: 0.9}} animate={{opacity: 1, scale: 1}}>
                            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
                            <h2 className="font-outfit font-bold text-3xl text-primary mb-4">Processing Payment...</h2>
                            <p className="font-dm-sans text-lg text-slate-600">Please wait while we confirm your payment</p>
                        </motion.div>
                    )}

                    {status === 'success' && (
                        <motion.div className="glass rounded-3xl p-12 text-center" initial={{opacity: 0, scale: 0.9}} animate={{opacity: 1, scale: 1}} data-testid="payment-success-message">
                            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                            <h2 className="font-outfit font-bold text-4xl text-primary mb-4">Payment Successful!</h2>
                            <p className="font-dm-sans text-lg text-slate-600 mb-8">Thank you for your payment. Your admission fee has been received successfully.</p>
                            {paymentDetails && (
                                <div className="bg-muted rounded-2xl p-6 mb-8 text-left">
                                    <h3 className="font-outfit font-bold text-xl mb-4 text-center">Payment Details</h3>
                                    <div className="space-y-2 font-dm-sans text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Transaction ID:</span>
                                            <span className="font-semibold">{paymentDetails.session_id.substring(0, 20)}...</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Amount:</span>
                                            <span className="font-semibold">{paymentDetails.currency.toUpperCase()} {(paymentDetails.amount_total / 100).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status:</span>
                                            <span className="font-semibold text-green-600 capitalize">{paymentDetails.payment_status}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/">
                                    <Button size="lg">Go to Homepage</Button>
                                </Link>
                                <Link to="/admissions">
                                    <Button size="lg" variant="outline">View Admission Status</Button>
                                </Link>
                            </div>
                        </motion.div>
                    )}

                    {(status === 'error' || status === 'timeout' || status === 'expired') && (
                        <motion.div className="glass rounded-3xl p-12 text-center" initial={{opacity: 0, scale: 0.9}} animate={{opacity: 1, scale: 1}}>
                            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                                <span className="text-red-600 text-4xl">×</span>
                            </div>
                            <h2 className="font-outfit font-bold text-3xl text-primary mb-4">
                                {status === 'expired' ? 'Payment Session Expired' : 'Payment Verification Failed'}
                            </h2>
                            <p className="font-dm-sans text-lg text-slate-600 mb-8">
                                {status === 'expired' ? 'Your payment session has expired. Please try again.' : 'We could not verify your payment. If amount was deducted, please contact us.'}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/admissions">
                                    <Button size="lg">Try Again</Button>
                                </Link>
                                <Link to="/contact">
                                    <Button size="lg" variant="outline">Contact Support</Button>
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;